import Badge from '@/components/public/ui/Badge';
import Icon from '@/components/public/ui/Icon';
import { eventDay, eventMonth, eventTime } from '@/lib/datetime';

export interface EventItem {
  id: number;
  title: string;
  description: string | null;
  location: string | null;
  category: string | null;
  startsAt: string | null;
  endsAt: string | null;
}

interface EventCardProps {
  event: EventItem;
}

const badgeFor: Record<string, 'primary' | 'secondary' | 'tertiary'> = {
  Assembleia: 'secondary',
  Cultural: 'primary',
  Desporto: 'tertiary',
  Infantil: 'primary',
};

function formatMonth(isoString: string): string {
  return eventMonth(isoString).toUpperCase();
}

// Display-only agenda card (events have no detail page).
export default function EventCard({ event }: EventCardProps) {
  const { title, location, category, startsAt } = event;
  const badge = badgeFor[category ?? ''] ?? 'primary';
  const isPast = startsAt ? new Date(startsAt) < new Date() : false;

  return (
    <article className="surface-card hover:border-line-strong flex h-full flex-col p-5 transition-all duration-200 hover:shadow-md">
      <div className="mb-4 flex items-start justify-between">
        <div>
          {startsAt && (
            <>
              <div className="font-headline text-primary text-4xl leading-none font-bold tracking-tight">
                {eventDay(startsAt)}
              </div>
              <div className="text-on-surface-light mt-1 text-xs font-semibold tracking-widest uppercase">
                {formatMonth(startsAt)}
              </div>
            </>
          )}
          {isPast && (
            <div className="badge-completed mt-2 rounded-full border px-2 py-0.5 text-center text-[10px] font-bold tracking-wide">
              Realizado
            </div>
          )}
        </div>
        {category && <Badge label={category} color={badge} />}
      </div>

      <h3 className="font-headline text-on-surface mb-3 leading-snug font-semibold">{title}</h3>

      {startsAt && (
        <div className="text-on-surface-light mb-1 flex items-center gap-2 text-sm">
          <Icon name="schedule" size="sm" className="text-primary" />
          {eventTime(startsAt)}
        </div>
      )}
      {location && (
        <div className="text-on-surface-light flex items-center gap-2 text-sm">
          <Icon name="location_on" size="sm" className="text-primary" />
          {location}
        </div>
      )}

      <div className="flex-1" />
    </article>
  );
}
